import { defineChart, dot, ruleX, ruleY } from "@tanstack/charts";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { BrandChartTheme, LineLegendItem } from "../BrandChart";
import { formatTickValue } from "../Charts.utils";
import { ScatterBenchmarkItem, ScatterItem } from "./Scatter.types";

export const AVERAGE_DASH = "4,4";

const mean = (values: number[]) =>
  values.reduce((sum, value) => sum + value, 0) / Math.max(1, values.length);

/** Averages of the plotted points; benchmarks are drawn but not averaged. */
export const getScatterAverages = (data: ScatterItem[]) => ({
  x: mean(data.map((item) => item.x)),
  y: mean(data.map((item) => item.y)),
});

export const getScatterLegend = (
  averageColor: string,
  benchmarks: ScatterBenchmarkItem[] = [],
): LineLegendItem[] => [
  { label: "Average", color: averageColor, strokeDasharray: AVERAGE_DASH },
  ...benchmarks.map((benchmark) => ({
    label: benchmark.label,
    color: benchmark.color,
    strokeDasharray: benchmark.strokeDasharray,
    strokeWidth: parseFloat(benchmark.strokeWidth) || 1.5,
  })),
];

/** Domain covering the points and any benchmark lines. */
const yDomain = (data: ScatterItem[], benchmarks: ScatterBenchmarkItem[]) => {
  const values = [
    ...data.map((item) => item.y),
    ...benchmarks.map((item) => item.value),
  ];

  return [Math.min(0, ...values), Math.max(...values)] as const;
};

export const buildScatterDefinition = ({
  data,
  brandTheme,
  averageColor,
  benchmarks = [],
  xAxisLabel,
  yAxisLabel,
}: {
  data: ScatterItem[];
  brandTheme: BrandChartTheme;
  averageColor: string;
  benchmarks?: ScatterBenchmarkItem[];
  xAxisLabel?: string;
  yAxisLabel?: string;
}) => {
  const averages = getScatterAverages(data);

  return defineChart({
    theme: brandTheme.theme,
    marks: [
      ruleY([averages.y], {
        y: (value) => value,
        stroke: averageColor,
        strokeDasharray: AVERAGE_DASH,
      }),
      ruleX([averages.x], {
        x: (value) => value,
        stroke: averageColor,
        strokeDasharray: AVERAGE_DASH,
      }),
      ...benchmarks.map((benchmark) =>
        ruleY([benchmark], {
          y: "value",
          stroke: benchmark.color,
          strokeDasharray: benchmark.strokeDasharray,
          strokeWidth: parseFloat(benchmark.strokeWidth) || 1.5,
        }),
      ),
      dot(data, {
        x: "x",
        y: "y",
        key: "label",
        r: 5,
        fill: brandTheme.primary,
        fillOpacity: 0.9,
      }),
    ],
    scales: {
      x: {
        scale: scaleLinear,
        nice: true,
        grid: true,
        axis: {
          line: false,
          label: xAxisLabel,
          ticks: { size: 0, format: (value: number) => formatTickValue(value) },
        },
      },
      y: {
        // A configured instance keeps benchmark lines in view.
        scale: scaleLinear().domain(yDomain(data, benchmarks)).nice(),
        grid: true,
        axis: {
          line: false,
          label: yAxisLabel,
          ticks: { size: 0, format: (value: number) => formatTickValue(value) },
        },
      },
    },
  });
};
